<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['viewErrors']);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();
const labels = useMapGetter('labels/getLabels');

const dialogRef = ref(null);

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const isProcessing = computed(
  () => props.campaign?.campaign_status === STATUS_PROCESSING
);
const isCompleted = computed(
  () => props.campaign?.campaign_status === STATUS_COMPLETED
);

const sentContacts = computed(() => {
  const processed = props.campaign?.processed_contacts || 0;
  const failed = props.campaign?.failed_contacts || 0;
  return processed - failed;
});

const scheduledDate = computed(() => {
  if (!props.campaign?.scheduled_at) return '-';
  return new Date(props.campaign.scheduled_at * 1000).toLocaleString();
});

const statusLabel = computed(() => {
  if (isProcessing.value) return t('CAMPAIGN.WHATSAPP.CARD.STATUS.PROCESSING');
  if (isCompleted.value) return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const statusColor = computed(() => {
  if (isProcessing.value) return 'text-n-brand bg-n-brand/10';
  if (isCompleted.value) return 'text-n-slate-11 bg-n-alpha-2';
  return 'text-n-teal-11 bg-n-teal-3';
});

const audienceLabels = computed(() => {
  const audience = props.campaign?.audience || [];
  if (!audience.length || !labels.value?.length) return [];

  return audience
    .filter(item => item.type === 'Label')
    .map(item => {
      const label = labels.value.find(l => l.id === item.id);
      return label
        ? { id: label.id, title: label.title, color: label.color }
        : null;
    })
    .filter(Boolean);
});

const handleViewErrors = () => {
  emit('viewErrors');
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.WHATSAPP.DETAILS.TITLE')"
    width="2xl"
    overflow-y-auto
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPAIGN.WHATSAPP.DETAILS.CLOSE')"
  >
    <div v-if="campaign" class="flex flex-col gap-4 overflow-hidden">
      <!-- Header -->
      <div class="flex items-start justify-between gap-3 min-w-0">
        <h3 class="text-lg font-medium text-n-slate-12 break-all min-w-0">
          {{ campaign.title }}
        </h3>
        <span
          class="text-xs font-medium px-2 py-1 rounded-md shrink-0"
          :class="statusColor"
        >
          {{ statusLabel }}
        </span>
      </div>

      <!-- Message -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.DETAILS.MESSAGE') }}
        </label>
        <div
          v-dompurify-html="
            formatMessage(campaign.message, false, false, false)
          "
          class="p-3 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12 [&>p]:mb-0"
        />
      </div>

      <!-- Info Grid -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Inbox -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.DETAILS.INBOX') }}
          </label>
          <span class="text-sm text-n-slate-12">
            {{ campaign.inbox?.name || '-' }}
          </span>
        </div>

        <!-- Scheduled -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.DETAILS.SCHEDULED_AT') }}
          </label>
          <span class="text-sm text-n-slate-12">
            {{ scheduledDate }}
          </span>
        </div>
      </div>

      <!-- Audience -->
      <div v-if="audienceLabels.length > 0" class="flex flex-col gap-2">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.DETAILS.AUDIENCE') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="label in audienceLabels"
            :key="label.id"
            class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-12"
          >
            <span
              class="size-2 rounded-full shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
          </span>
        </div>
      </div>

      <!-- Delivery Funnel Metrics -->
      <div v-if="isCompleted || isProcessing" class="flex flex-col gap-2">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.DETAILS.METRICS') }}
        </label>
        <div class="grid grid-cols-3 gap-3 sm:grid-cols-6">
          <div class="flex flex-col items-center p-3 rounded-lg bg-n-alpha-2">
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ campaign.total_contacts || 0 }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.TOTAL_CONTACTS') }}
            </span>
          </div>
          <div class="flex flex-col items-center p-3 rounded-lg bg-n-teal-2">
            <span class="text-2xl font-semibold text-n-teal-11">
              {{ sentContacts }}
            </span>
            <span class="text-xs text-n-teal-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.SENT') }}
            </span>
          </div>
          <div class="flex flex-col items-center p-3 rounded-lg bg-n-blue-2">
            <span class="text-2xl font-semibold text-n-blue-11">
              {{ campaign.delivered_contacts || 0 }}
            </span>
            <span class="text-xs text-n-blue-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.DELIVERED') }}
            </span>
          </div>
          <div class="flex flex-col items-center p-3 rounded-lg bg-n-violet-2">
            <span class="text-2xl font-semibold text-n-violet-11">
              {{ campaign.read_contacts || 0 }}
            </span>
            <span class="text-xs text-n-violet-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.READ') }}
            </span>
          </div>
          <div class="flex flex-col items-center p-3 rounded-lg bg-n-green-2">
            <span class="text-2xl font-semibold text-n-green-11">
              {{ campaign.replied_contacts || 0 }}
            </span>
            <span class="text-xs text-n-green-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.REPLIED') }}
            </span>
          </div>
          <button
            v-if="campaign.failed_contacts > 0"
            type="button"
            class="flex flex-col items-center p-3 rounded-lg bg-n-ruby-2 cursor-pointer hover:bg-n-ruby-3 transition-colors"
            @click="handleViewErrors"
          >
            <span class="text-2xl font-semibold text-n-ruby-11">
              {{ campaign.failed_contacts }}
            </span>
            <span class="text-xs text-n-ruby-11 underline">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.FAILED') }}
            </span>
          </button>
          <div
            v-else
            class="flex flex-col items-center p-3 rounded-lg bg-n-alpha-2"
          >
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ campaign.failed_contacts || 0 }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.DETAILS.FAILED') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
